import { useState } from 'react';
import {
  Search,
  MoreVertical,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  Trash2,
  AlertTriangle,
  Loader2,
  Users,
  Gauge,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminLayout } from '@/components/admin';
import { Card, Button, Badge, Avatar } from '@/components/ui';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import type { User } from '@/types';

const DEFAULT_DAILY_QUOTA = 10;

const AdminUsers = () => {
  const { t } = useTranslation();
  const {
    users,
    loading,
    error,
    search,
    total,
    handleSearch,
    makeAdmin,
    removeAdmin,
    enableUser,
    disableUser,
    remove,
    updateQuota,
  } = useAdminUsers();

  const [searchInput, setSearchInput] = useState(search);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<User | null>(null);
  const [quotaModal, setQuotaModal] = useState<User | null>(null);
  const [quotaInput, setQuotaInput] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchInput);
  };

  const handleAction = async (action: () => Promise<unknown>, userId: string) => {
    setActionLoading(userId);
    setOpenDropdown(null);
    try {
      await action();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setActionLoading(deleteModal.uid);
    try {
      await remove(deleteModal.uid);
      setDeleteModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleQuotaSubmit = async () => {
    if (!quotaModal) return;
    const value = parseInt(quotaInput, 10);
    if (isNaN(value) || value < 0) return;
    setActionLoading(quotaModal.uid);
    try {
      await updateQuota(quotaModal.uid, value);
      setQuotaModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const openQuotaModal = (user: User) => {
    setOpenDropdown(null);
    setQuotaInput(String(user.dailyDeeplinkQuota ?? DEFAULT_DAILY_QUOTA));
    setQuotaModal(user);
  };

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return '-';
    const date = (timestamp as { toDate: () => Date }).toDate?.() || new Date(timestamp as string);
    return date.toLocaleDateString();
  };

  const formatQuota = (user: User) => {
    const quota = user.dailyDeeplinkQuota ?? DEFAULT_DAILY_QUOTA;
    return quota === 0 ? t('admin.quotaUnlimited') : String(quota);
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">
          {t('admin.usersTitle')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          {t('admin.usersSubtitle', { count: total })}
        </p>
      </div>

      {/* Search */}
      <Card variant="default" padding="md" className="mb-6">
        <form onSubmit={handleSearchSubmit} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('admin.searchUsersPlaceholder')}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:text-white dark:placeholder-gray-400"
            />
          </div>
          <Button type="submit" variant="primary">
            {t('common.search')}
          </Button>
        </form>
      </Card>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Users Table */}
      <Card variant="default" padding="none">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">{t('admin.noUsersFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.user')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.username')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.role')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.status')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.quota')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.joined')}
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.uid} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar
                          src={user.photoURL || undefined}
                          name={user.displayName || user.email}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-dark-800 dark:text-white truncate">
                            {user.displayName || '-'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-lime-600 dark:text-lime-400">@{user.username || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {user.role === 'admin' ? (
                        <Badge variant="error" size="sm">
                          <Shield className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge variant="dark" size="sm">User</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.disabled ? (
                        <Badge variant="error" size="sm">{t('admin.disabled')}</Badge>
                      ) : (
                        <Badge variant="lime" size="sm">{t('admin.active')}</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openQuotaModal(user)}
                        className="text-sm font-medium text-dark-800 dark:text-white hover:text-lime-600 dark:hover:text-lime-400 transition-colors"
                        title={t('admin.editQuota')}
                      >
                        {formatQuota(user)}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end relative">
                        {actionLoading === user.uid ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : (
                          <>
                            <button
                              onClick={() => setOpenDropdown(openDropdown === user.uid ? null : user.uid)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-gray-400" />
                            </button>

                            {openDropdown === user.uid && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setOpenDropdown(null)}
                                />
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 z-20">
                                  {user.role === 'admin' ? (
                                    <button
                                      onClick={() => handleAction(() => removeAdmin(user.uid), user.uid)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <ShieldOff className="w-4 h-4" />
                                      {t('admin.removeAdmin')}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleAction(() => makeAdmin(user.uid), user.uid)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <Shield className="w-4 h-4" />
                                      {t('admin.makeAdmin')}
                                    </button>
                                  )}

                                  <button
                                    onClick={() => openQuotaModal(user)}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <Gauge className="w-4 h-4" />
                                    {t('admin.editQuota')}
                                  </button>

                                  {user.disabled ? (
                                    <button
                                      onClick={() => handleAction(() => enableUser(user.uid), user.uid)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <UserCheck className="w-4 h-4" />
                                      {t('admin.enableUser')}
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleAction(() => disableUser(user.uid), user.uid)}
                                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      <UserX className="w-4 h-4" />
                                      {t('admin.disableUser')}
                                    </button>
                                  )}

                                  <hr className="my-1 border-gray-100 dark:border-gray-700" />

                                  <button
                                    onClick={() => {
                                      setOpenDropdown(null);
                                      setDeleteModal(user);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {t('admin.deleteUser')}
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quota Edit Modal */}
      {quotaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setQuotaModal(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-lime-100 dark:bg-lime-900/40 rounded-2xl flex items-center justify-center">
                <Gauge className="w-6 h-6 text-lime-600 dark:text-lime-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white">
                  {t('admin.editQuota')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {quotaModal.displayName || quotaModal.email}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.quotaLabel')}
              </label>
              <input
                type="number"
                min="0"
                value={quotaInput}
                onChange={(e) => setQuotaInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleQuotaSubmit()}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500/20 focus:border-lime-500 dark:text-white text-lg"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                {t('admin.quotaHint')}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setQuotaModal(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleQuotaSubmit}
                disabled={actionLoading === quotaModal.uid}
              >
                {actionLoading === quotaModal.uid ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteModal(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/40 rounded-2xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white">
                  {t('admin.deleteUserConfirmTitle')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.deleteUserConfirmDescription')}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <Avatar
                  src={deleteModal.photoURL || undefined}
                  name={deleteModal.displayName || deleteModal.email}
                  size="sm"
                />
                <div>
                  <p className="font-medium text-dark-800 dark:text-white">
                    {deleteModal.displayName || deleteModal.email}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">@{deleteModal.username}</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {t('admin.deleteUserWarning')}
            </p>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setDeleteModal(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleDelete}
                disabled={actionLoading === deleteModal.uid}
              >
                {actionLoading === deleteModal.uid ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('admin.deleteUser')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers;
